/**
 * IDMS Analysis Executor
 *
 * Handles the 'idms-analysis' and 'pipeline-idms-analysis' node execution.
 * Calls the ModernizeIT API Layer /idmsanalysis endpoint.
 *
 * API Flow (WORKER MODE):
 * 1. Verify upstream ingest job is 'completed' (poll if needed)
 * 2. POST /idmsanalysis -> returns job_id with status QUEUED
 * 3. Poll GET /jobs/{job_id} -> wait until status = 'completed'
 * 4. Extract results from job.output_data
 *
 * CRITICAL: Must wait for upstream ingest to complete before starting.
 * IDMS Analysis requires COBOL V3 listings (.lst) from ingest.
 */

import { pollJobUntilComplete } from '../../utils/jobPoller.js'
import { getAuthHeaders, handleApiError } from './authHelper.js'

/**
 * Execute the IDMS analysis node
 *
 * @param {Object} config - Node configuration
 * @param {Object} context - Workflow execution context
 * @param {Function} onProgress - Progress callback (progress: 0-100, message: string)
 * @param {AbortSignal} signal - Abort signal for cancellation
 * @returns {Promise<Object>} - Execution result with job info and artifacts
 */
export async function execute(config, context, onProgress = () => {}, signal = null) {
  const apiBaseUrl = context.api_base_url

  onProgress(0, 'Preparing IDMS analysis...')

  // Check for abort before starting
  if (signal?.aborted) {
    throw new Error('Execution cancelled')
  }

  // 1. Verify upstream ingest job exists
  const ingestJobId = context.jobs?.ingest_job_id
  if (!ingestJobId) {
    throw new Error('IDMS Analysis requires ingest job ID from Upload node. Run Upload first.')
  }

  // 2. Verify upstream ingest job is completed (poll if needed)
  console.log(`[IdmsAnalysisExecutor] Verifying upstream ingest job ${ingestJobId} is completed...`)
  onProgress(2, 'Waiting for Upload to complete...')

  try {
    await pollJobUntilComplete(ingestJobId, apiBaseUrl, {
      maxWaitSeconds: 600,
      pollInterval: 1000,
      onProgress: (progress, message) => {
        const mappedProgress = 2 + (progress * 0.06)
        onProgress(mappedProgress, `Waiting for Upload: ${message}`)
      },
      signal
    })
    console.log(`[IdmsAnalysisExecutor] Upstream ingest job ${ingestJobId} is completed`)
  } catch (error) {
    throw new Error(`Upstream Upload job failed: ${error.message}`)
  }

  onProgress(8, 'Upload complete, starting IDMS analysis...')

  // 3. Build the request body
  const requestBody = {
    scout_account_id: context.scout_account_id,
    application_name: context.application_name,
    user_id: context.user_id,
    ingest_job_id: ingestJobId,
  }

  onProgress(10, 'Submitting IDMS analysis job...')

  // 4. Submit the job
  let jobResponse
  try {
    const response = await fetch(`${apiBaseUrl}/idmsanalysis`, {
      method: 'POST',
      headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(requestBody),
      signal,
    })

    if (!response.ok) {
      await handleApiError(response)
    }

    jobResponse = await response.json()

    if (jobResponse.success === false) {
      throw new Error(jobResponse.error || 'IDMS analysis failed')
    }

  } catch (error) {
    if (error.name === 'AbortError') {
      throw new Error('Execution cancelled')
    }
    if (error.message.includes('Failed to fetch')) {
      throw new Error(`Cannot connect to API at ${apiBaseUrl}. Is the server running?`)
    }
    throw error
  }

  const jobId = jobResponse.job_id
  if (!jobId) {
    throw new Error('API did not return a job_id')
  }

  console.log(`[IdmsAnalysisExecutor] Job created: ${jobId}, polling until completed...`)
  onProgress(15, `Job started: ${jobId}`)

  // 5. Poll for completion using generic jobs endpoint
  const completedJob = await pollJobUntilComplete(jobId, apiBaseUrl, {
    maxWaitSeconds: 600,
    pollInterval: 1000,
    onProgress: (progress, message) => {
      const mappedProgress = 15 + (progress * 0.80)
      onProgress(mappedProgress, message)
    },
    signal
  })

  console.log('[IdmsAnalysisExecutor] Job completed, extracting results from output_data')
  onProgress(95, 'Extracting results...')

  // 6. Extract results from job.output_data (worker mode)
  const outputData = completedJob.output_data
  if (!outputData) {
    throw new Error('Job completed but has no output_data')
  }

  onProgress(100, 'IDMS analysis complete')

  // 7. Return formatted result
  return formatResult(jobId, outputData, context.jobs)
}

/**
 * Format the result object for consistent structure
 */
function formatResult(jobId, data, contextJobs = {}) {
  console.log('[IdmsAnalysisExecutor] formatResult called with contextJobs:', contextJobs)

  const resultJobs = {
    ...contextJobs,
    idms_analysis_job_id: jobId,
  }

  console.log('[IdmsAnalysisExecutor] Returning jobs:', resultJobs)

  return {
    // Job tracking — preserve upstream job IDs for downstream nodes
    jobs: resultJobs,

    // Status
    success: data.success !== false,
    status: data.status || 'completed',

    // Summary statistics
    summary: data.summary || {},

    // Graph loading results
    graph: data.graph || null,

    // Execution metrics
    duration_ms: data.duration_ms,

    // Error info if any
    error: data.error || null,
  }
}

export default {
  execute,
}
